import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../lib/api";

/**
 * Sessions sidebar state for Copilot: list, active id, load history,
 * delete session, and a stable ref for the currently active session id.
 * `onNewMessages(msgs)` is called after a session is (re)loaded so the
 * consumer can replace its own messages state.
 */
export default function useCopilotSessions({ onNewMessages }) {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const sessionRef = useRef(null);

  const refreshSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/copilot/sessions");
      setSessions(data || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Copilot: failed to load sessions", err);
    }
  }, []);

  useEffect(() => { refreshSessions(); }, [refreshSessions]);

  const clearActive = useCallback(() => {
    sessionRef.current = null;
    setActiveSessionId(null);
  }, []);

  const setActive = useCallback((sid) => {
    sessionRef.current = sid;
    setActiveSessionId(sid);
  }, []);

  const loadSession = useCallback(async (sid) => {
    setActive(sid);
    try {
      const { data } = await api.get(`/copilot/history?session_id=${encodeURIComponent(sid)}`);
      onNewMessages(data || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Copilot: failed to load history", err);
      onNewMessages([]);
    }
  }, [onNewMessages, setActive]);

  const deleteSession = useCallback(async (sid) => {
    try {
      await api.delete(`/copilot/sessions/${encodeURIComponent(sid)}`);
      if (sessionRef.current === sid) clearActive();
      refreshSessions();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Copilot: failed to delete session", err);
    }
  }, [refreshSessions, clearActive]);

  return {
    sessions,
    activeSessionId,
    sessionRef,
    setActive,
    clearActive,
    loadSession,
    deleteSession,
    refreshSessions,
  };
}
